// Optional metadata on the existing Director plan. This adapter never requests
// a provider, invents a score, starts a day, or changes another plan field.
const object = value => !!value && typeof value === 'object' && !Array.isArray(value)
const validMoveId = value => typeof value === 'string' && /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,63}$/.test(value) && !['constructor', 'prototype', '__proto__'].includes(value)
const ignored = reason => ({ ok: true, ignored: true, reason, events: [] })

export const WORLD_SIM_APPRAISAL_SCHEMA = {
  type: 'object', additionalProperties: false, required: ['moves', 'confidence'],
  properties: {
    moves: { type: 'array', minItems: 1, maxItems: 2, uniqueItems: true,
      items: { type: 'string', pattern: '^[a-zA-Z0-9][a-zA-Z0-9_-]{0,63}$' } },
    confidence: { type: 'number', minimum: 0, maximum: 1 },
    outcome: { enum: ['accept', 'defer', 'refuse'] },
  },
}

// The shared plan validator can repair this optional field independently of
// valid dialogue. Runtime-specific IDs are filtered again at the final boundary.
export function normalizeWorldSimAppraisal(raw, knownMoves) {
  try {
    if (!object(raw) || Object.keys(raw).some(k => !['moves', 'confidence', 'outcome'].includes(k))
      || !Array.isArray(raw.moves) || raw.moves.length > 64 || !Number.isFinite(raw.confidence)) return null
    const known = knownMoves === undefined ? null : new Set([...knownMoves].map(m => typeof m === 'string' ? m : m?.id))
    const moves = [...new Set(raw.moves.filter(id => validMoveId(id) && (!known || known.has(id))))].slice(0, 2)
    if (!moves.length) return null
    return { moves, confidence: Math.max(0, Math.min(1, raw.confidence)),
      ...(['accept', 'defer', 'refuse'].includes(raw.outcome) ? { outcome: raw.outcome } : {}) }
  } catch { return null }
}

// Stable per-block vocabulary can join the cached prompt prefix. Live scores,
// stances, spirit causes and active pressure remain in runtime.snapshot().
export function worldSimAppraisalContext(runtime) {
  if (!runtime || runtime.enabled !== true || runtime.disposed || !runtime.block) return null
  return {
    confidenceThreshold: runtime.block.confidenceThreshold ?? 0.6,
    moves: (runtime.block.moves || []).map(({ id, hint }) => ({ id, hint })),
  }
}

// Call once for the user's current, validated response after its epoch guard.
// Passing the revision captured before engine.plan also rejects an appraisal
// computed before a normal UI command changed the authoritative sim state.
export function applyWorldSimAppraisal(sd, plan, { isCurrent, expectedRevision } = {}) {
  const runtime = sd?.['world-sim']
  if (!runtime || runtime.enabled !== true || runtime.disposed || sd?._ended || sd?._reusableDisposed) return ignored('worldSim is not active.')
  try {
    if (typeof isCurrent === 'function' && isCurrent() !== true) return ignored('This response is no longer current.')
    if (expectedRevision !== undefined && (!Number.isSafeInteger(expectedRevision) || runtime.serialize?.()?.revision !== expectedRevision)) return ignored('worldSim changed while this response was being planned.')
    const view = runtime.view()
    if (!view.started || view.ended) return ignored('There is no active worldSim day.')
    const appraisal = normalizeWorldSimAppraisal(plan?.appraisal, runtime.block?.moves || [])
    if (!appraisal) return ignored('No valid worldSim appraisal was returned.')
    const result = runtime.apply({ type: 'appraisal', appraisal })
    return object(result) && typeof result.ok === 'boolean' ? result : { ok: false, reason: 'worldSim did not return an action result.', events: [] }
  } catch (error) { return { ok: false, reason: error?.message || 'worldSim could not resolve this appraisal.', events: [] } }
}
